import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface QueueTime {
  attractionName: string;
  entityType: string;
  waitTimeMinutes: number;
  status: string;
  minutesSinceUpdate: number;
  completed: boolean;
  rating: number;
  priority: string;
}

export function useQueueTimes(visitId: string | null) {
  return useQuery({
    queryKey: ['queue-times', visitId],
    queryFn: async () => {
      if (!visitId) throw new Error('Missing visit ID');
      const response = await fetch(`${API_BASE}/api/visits/${visitId}/queue-times`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<QueueTime[]>;
    },
    enabled: !!visitId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}


export function useAddAttraction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visitId, attractionName }: { visitId: string; attractionName: string }) => {
      const response = await fetch(`${API_BASE}/api/visits/${visitId}/attractions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attractionName }),
      });
      if (!response.ok) throw new Error('Failed to add attraction');
      return response.json();
    },
    onSuccess: (_, { visitId }) => {
      queryClient.invalidateQueries({ queryKey: ['queue-times', visitId] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useRemoveAttraction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visitId, attractionName }: { visitId: string; attractionName: string }) => {
      const response = await fetch(`${API_BASE}/api/visits/${visitId}/attractions`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attractionName }),
      });
      if (!response.ok) throw new Error('Failed to remove attraction');
      return response.json();
    },
    onSuccess: (_, { visitId }) => {
      queryClient.invalidateQueries({ queryKey: ['queue-times', visitId] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useReorderAttractions(visitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attractionNames: string[]) =>
      fetch(`${API_BASE}/api/visits/${visitId}/attractions/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attractionNames }),
      }).then(res => {
        if (!res.ok) throw new Error('Failed to reorder attractions');
        return res.json();
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-times', visitId] });
      queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
    },
  });
}

export function useToggleComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visitId, attractionName, completed }: { visitId: string; attractionName: string; completed: boolean }) => {
      const method = completed ? 'POST' : 'DELETE';
      const response = await fetch(`${API_BASE}/api/visits/${visitId}/attractions/complete`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attractionName }),
      });
      if (!response.ok) throw new Error('Failed to toggle attraction completion');
      return response.json();
    },
    onSuccess: (_, { visitId }) => {
      queryClient.invalidateQueries({ queryKey: ['queue-times', visitId] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}
